/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useRef, useState } from "react";
import useClickOutside from "../../hook/useClickOutside";
import { getScrollParent } from "../../utils/DOMHelper";
import StyledSelect from "../StyledSelect";
interface ContainerProps {
  disabled?: boolean;
}

const Container = styled.div<ContainerProps>`
  width: 100%;
  height: 40px;
  color: #2f2f2f;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`;

const InputContainer = styled.input<{ isDuration: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: ${(props) => (props.isDuration ? "40%" : "100%")};
  padding: 10px;
  font-size: 16px;
  border-radius: 5px;
  border: solid 1px #cccccc;
  background-color: #ffffff;

  /* Chrome, Safari, Edge, Opera */
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  [type="number"] {
    -moz-appearance: textfield;
  }
`;

interface DropViewProps {
  position: { left: number; top: number };
  width: number;
  offset: number;
}

const DropView = styled.div<DropViewProps>`
  position: fixed;
  background: #fff;
  z-index: 20;
  top: ${(props) => (props.offset > 0 ? `auto` : `${props.position.top}px`)};
  bottom: ${(props) => (props.offset > 0 ? `${props.offset}px` : "auto")};
  width: ${(props) => props.width}px;
  max-height: 200px;
  flex: 1;
  border-radius: 5px;
  box-shadow: 0px 3px 6px #00000033;
  overflow-y: auto;
  padding: 10px 0px;
  z-index: 10;
`;

interface DropItemProps {
  preSelected: boolean;
  selected: boolean;
}

const DropItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 5px;
  cursor: pointer;

  :hover {
    background-color: #fb972e;
    color: #fff;
  }
`;

const Name = styled.div`
  font-size: 16px;
  padding: 10px;
`;

interface Option {
  id: string;
  name: string;
  value: string;
}

interface Props {
  options: Option[];
  selectedOption: string | undefined;
  handleSelectedOption: (vle: string) => void;
  disabled?: boolean;
  above?: boolean;
  customCss?: any;
  isDuration?: boolean;
  clearAll?: boolean;
}

const NO_OPTION = null;

const TIME_UNIT_OPTIONS: TimeUnitOption[] = [
  {
    id: "0",
    name: "Minute(s)",
  },
  {
    id: "1",
    name: "Hour(s)",
  },
];

/**
 * Our own Select implementation. DropView is the Select body when expanded,
 * and it's position: fixed.
 *
 * It get's it's `top` and `left` properties calculated whenever I click on it's,
 * sibling input. Triggering it's view.
 * @param props
 */

export default function StyledWritableSelect(props: Props): JSX.Element {
  const {
    options,
    selectedOption,
    handleSelectedOption,
    above,
    customCss,
    disabled,
    isDuration,
    clearAll,
  } = props;

  /**
   * Select functionality
   */
  const [openDropView, setOpenDropView] = useState<boolean>(false);
  const [preSelectedOption, setPreSelectedSearchId] = useState<
    string | typeof NO_OPTION
  >(NO_OPTION);

  const [offsetPopover, setOffsetPopover] = useState(0);

  /**
   * Refs to keep track
   */
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dropViewRef = useRef<HTMLDivElement | null>(null);
  const [inputValue, setInputValue] = useState<string>();

  // Ref so we're able to scroll the items into view
  const dropItemRefs = useRef<
    {
      id: string;
      element: HTMLDivElement | null;
    }[]
  >([]);

  const [dropviewPosition, setDropViewPosition] = useState<{
    left: number;
    top: number;
  }>({
    left: 0,
    top: 0,
  });
  const [dropViewWidth, setDropViewWidth] = useState<number>(0);
  const [timeUnit, setTimeUnit] = useState<TimeUnitOption>(
    TIME_UNIT_OPTIONS[0]
  );

  function updateDropViewPosition() {
    const elementBoundingRect = (
      wrapperRef.current as HTMLDivElement
    ).getBoundingClientRect();

    setDropViewPosition({
      top: elementBoundingRect.top + elementBoundingRect.height,
      left: elementBoundingRect.left,
    });

    setDropViewWidth(elementBoundingRect.width);
  }

  useEffect(() => {
    if (!clearAll) return;
    setInputValue("");
    setTimeUnit(TIME_UNIT_OPTIONS[0]);
  }, [clearAll]);

  useEffect(() => {
    if (!dropViewRef.current || !wrapperRef.current) return;

    const elementBoundingRect = (
      dropViewRef.current as HTMLDivElement
    ).getBoundingClientRect();

    const containerBoundingRect = (
      wrapperRef.current as HTMLDivElement
    ).getBoundingClientRect();

    let bottom = elementBoundingRect.bottom;

    if (offsetPopover !== 0)
      bottom =
        bottom + elementBoundingRect.height + containerBoundingRect.height;

    if (bottom >= 0 && bottom >= window.innerHeight) {
      setOffsetPopover(window.innerHeight - containerBoundingRect.top);
    } else {
      setOffsetPopover(0);
    }
  }, [dropViewRef, openDropView, selectedOption]);

  function onInputClicked() {
    updateDropViewPosition();
    setOpenDropView((openDrop) => !openDrop);
  }

  useEffect(() => {
    if (!openDropView) return () => {};

    function closeDropView() {
      setOpenDropView(false);
    }

    const scrollParent = getScrollParent(
      containerRef.current,
      containerRef.current
    );

    if (!scrollParent) return () => {};

    scrollParent.addEventListener("scroll", closeDropView);

    return () => {
      scrollParent.removeEventListener("scroll", closeDropView);
    };
  }, [wrapperRef, containerRef, openDropView]);

  useEffect(() => {
    function closeDropView() {
      setOpenDropView(false);
    }

    window.addEventListener("resize", closeDropView);
    return () => window.removeEventListener("resize", closeDropView);
  }, [wrapperRef, containerRef]);

  useEffect(() => {
    setOpenDropView(false);
  }, [selectedOption]);

  return (
    <Container ref={containerRef} css={customCss} disabled={disabled}>
      <div ref={wrapperRef} style={{ display: "flex", alignItems: "center" }}>
        <InputContainer
          type="number"
          id="writableSelect"
          value={inputValue}
          onClick={() => !disabled && onInputClicked()}
          onChange={(e) => {
            if (e.target.value) {
              setOpenDropView(false);
            } else {
              setOpenDropView(true);
            }
            setInputValue(e.target.value);
            if (timeUnit.id === "0") {
              handleSelectedOption(e.target.value);
            } else {
              handleSelectedOption(`${+e.target.value * 60}`);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
              e.preventDefault();
            }
          }}
          onWheel={() => {
            document.getElementById("writableSelect")?.blur();
          }}
          isDuration={isDuration}
        />
        {isDuration && (
          <StyledSelect
            options={TIME_UNIT_OPTIONS}
            selectedOption={timeUnit}
            handleSelectedOption={(opt) => {
              setTimeUnit(opt);
              if (opt.id === "0") {
                handleSelectedOption(inputValue);
              } else {
                handleSelectedOption(`${+inputValue * 60}`);
              }
            }}
            customCss={{
              marginLeft: "10px",
            }}
          />
        )}
      </div>

      {openDropView && (
        <DropView
          position={dropviewPosition}
          offset={offsetPopover}
          width={dropViewWidth}
          ref={dropViewRef}
        >
          {options.map((option, index) => {
            return (
              <DropItem
                key={`option_${option.id}`}
                ref={(r) => {
                  dropItemRefs.current[index] = {
                    id: option.id,
                    element: r,
                  };
                }}
                onMouseEnter={() => setPreSelectedSearchId(option.id)}
                onMouseLeave={() => setPreSelectedSearchId(NO_OPTION)}
                onMouseDown={() => {
                  handleSelectedOption(option.value!);
                  if (+option.value >= 60) {
                    setInputValue(`${(+option.value / 60).toFixed(1)}`);
                    setTimeUnit(TIME_UNIT_OPTIONS[1]);
                  } else {
                    setInputValue(option.value);
                    setTimeUnit(TIME_UNIT_OPTIONS[0]);
                  }
                }}
              >
                <Name>{option.name}</Name>
              </DropItem>
            );
          })}
        </DropView>
      )}
    </Container>
  );
}

interface TimeUnitOption {
  id: string;
  name: string;
}
